import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// Use same-origin API calls on custom domains
const configuredUrl = process.env.REACT_APP_BACKEND_URL || '';
const isCustomDomain = typeof window !== 'undefined'
  && configuredUrl
  && window.location.origin !== configuredUrl
  && !window.location.hostname.includes('emergent')
  && !window.location.hostname.includes('localhost')
  && !window.location.hostname.includes('127.0.0.1');
const API_URL = isCustomDomain ? '' : configuredUrl;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Format API error for display
const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated, object = authenticated
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setUser(false);
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setUser(response.data);
    } catch (err) {
      localStorage.removeItem('access_token');
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // HIPAA: Auto session timeout after 15 minutes of inactivity
  useEffect(() => {
    if (!user || user === false) return;
    
    let timeoutId;
    const SESSION_TIMEOUT = 15 * 60 * 1000; // 15 minutes
    
    const resetTimer = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        console.warn('HIPAA: Session expired due to inactivity');
        localStorage.removeItem('access_token');
        setUser(false);
        window.location.href = '/login?expired=1';
      }, SESSION_TIMEOUT);
    };
    
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, resetTimer));
    resetTimer();
    
    return () => {
      clearTimeout(timeoutId);
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [user]);

  const login = async (email, password) => {
    setError(null);
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      
      // HIPAA 2FA: Check if OTP is required
      if (response.data.requires_otp) {
        return { 
          success: false, 
          requires_otp: true, 
          user_id: response.data.user_id,
          email: response.data.email,
          name: response.data.name,
          is_new_device: response.data.is_new_device,
          must_change_password: response.data.must_change_password,
          plan_expired: response.data.plan_expired,
          tenant_name: response.data.tenant_name,
          has_biometric: response.data.has_biometric,
        };
      }
      
      // 2FA disabled - direct login with token
      localStorage.setItem('access_token', response.data.access_token);
      setUser(response.data);
      return { success: true, user: response.data, must_change_password: response.data.must_change_password || false };
    } catch (err) {
      let errorMessage;
      if (err.response?.data?.detail) {
        errorMessage = formatApiErrorDetail(err.response.data.detail);
      } else if (err.response?.status) {
        errorMessage = `Server error (${err.response.status}). Please try again.`;
      } else if (err.code === 'ERR_NETWORK') {
        errorMessage = "Cannot connect to server. Please check your connection.";
      } else {
        errorMessage = err.message || "Something went wrong. Please try again.";
      }
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const verifyOtp = async (userId, otpCode) => {
    setError(null);
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/verify-otp`,
        { user_id: userId, otp_code: otpCode },
        { withCredentials: true }
      );
      localStorage.setItem('access_token', response.data.access_token);
      setUser(response.data);
      return { success: true, user: response.data, must_change_password: response.data.must_change_password || false };
    } catch (err) {
      const errorMessage = formatApiErrorDetail(err.response?.data?.detail) || "Invalid OTP. Please try again.";
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const resendOtp = async (userId) => {
    try {
      await axios.post(`${API_URL}/api/auth/resend-otp`, { user_id: userId });
      return { success: true };
    } catch (err) {
      return { success: false, error: "Failed to resend OTP" };
    }
  };

  const register = async (userData) => {
    setError(null);
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/register`,
        userData,
        { withCredentials: true }
      );
      localStorage.setItem('access_token', response.data.access_token);
      setUser(response.data);
      return { success: true };
    } catch (err) {
      const errorMessage = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('access_token');
      setUser(false);
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    verifyOtp,
    resendOtp,
    register,
    logout,
    checkAuth,
    isAuthenticated: !!user && user !== false,
    isSuperAdmin: user?.role === 'super_admin',
    isHospitalAdmin: user?.role === 'hospital_admin',
    isClaimManager: ['super_admin', 'hospital_admin', 'claim_manager'].includes(user?.role),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
